/**
 * HQ Sales Report pages. Each page is a thin wrapper around ReportShell
 * supplying its own endpoint, columns and CSV filename.
 */
import { useState } from 'react';
import { ReportShell, money, num, dateFmt } from './_shared';
import { SalesSummaryCards, StatusPill } from './SalesSummaryCards';
import { formatDate } from '@/utils/dateFormat';

// ---------------------------------------------------------------------------
// 1. All Sales
// ---------------------------------------------------------------------------
export function AllSalesReport() {
  return (
    <ReportShell
      title="All Sales — Bill-wise"
      url="/reports/sales/all"
      responseKey="bills"
      csvName="all-sales"
      renderSummary={(d) => (
        <>
          <SalesSummaryCards summary={d?.summary} />
          {d?.listed < d?.count && (
            <p className="text-xs text-amber-700 mb-2" data-testid="all-sales-truncated">Showing latest {num(d.listed)} of {num(d.count)} bills — totals above cover all {num(d.count)}. Narrow the date range to list every bill.</p>
          )}
        </>
      )}
      columns={[
        { key: () => null, label: '#', format: (_v, _r, i) => <span className="text-gray-400 tabular-nums">{i + 1}</span>, className: 'w-10' },
        { key: 'bill_datetime', label: 'Date & Time', format: (v, r) => <span className="whitespace-nowrap tabular-nums">{formatDate(v || r.created_at)}</span>, csvFormat: (v, r) => formatDate(v || r.created_at) },
        { key: 'bill_number', label: 'Bill No.', format: (v) => <span className="font-mono text-xs font-semibold text-gray-800">{v}</span>, csvFormat: (v) => v },
        { key: 'store_code', label: 'Store', format: (v, r) => <span title={r.store_name}>{v || r.store_name}</span>, csvFormat: (v, r) => v || r.store_name },
        { key: 'cashier_name', label: 'Cashier' },
        { key: 'customer_name', label: 'Customer', format: (v, r) => v || r.customer_phone || 'Walk-in' },
        { key: 'items_qty', label: 'Items', align: 'right', format: (v, r) => `${num(v)}${r.items_count ? ` (${r.items_count})` : ''}`, csvFormat: (v) => v },
        { key: 'payment_modes', label: 'Payment', format: (v) => (v || []).map(m => m.toUpperCase()).join(' + ') || '—', csvFormat: (v) => (v || []).join('+') },
        { key: 'status', label: 'Status', format: (v) => <StatusPill status={v} />, csvFormat: (v) => v || 'completed' },
        { key: 'total_discount', label: 'Discount', align: 'right', format: money },
        { key: 'excl_tax', label: 'Excl. Tax', align: 'right', format: money },
        { key: 'total_tax', label: 'Tax', align: 'right', format: money },
        { key: 'grand_total', label: 'Incl. Tax', align: 'right', format: (v) => <span className="font-semibold">{money(v)}</span>, csvFormat: (v) => v },
      ]}
      rowClassName={(r) => (r.status === 'returned' ? 'bg-red-50/40' : r.status === 'partially_returned' ? 'bg-amber-50/40' : '')}
      totalsRow={(d) => ['', 'TOTAL', '', '', '', '', num(d?.summary?.items_qty), '', `${num(d?.count)} bills`, money(d?.total_discount), money(d?.total_excl_tax), money(d?.total_tax), money(d?.total_incl_tax)]}
    />
  );
}

// ---------------------------------------------------------------------------
// 2. Store / Franchise wise
// ---------------------------------------------------------------------------
export function StoreWiseReport() {
  return (
    <ReportShell
      title="Store / Franchise wise Sales"
      url="/reports/sales/store-wise"
      responseKey="rows"
      csvName="store-wise-sales"
      showStoreFilter={false}
      columns={[
        { key: 'store_code', label: 'Store Code' },
        { key: 'store_name', label: 'Store Name' },
        { key: 'bill_count', label: 'Bills', align: 'right', format: num },
        { key: 'total_discount', label: 'Discount', align: 'right', format: money },
        { key: 'total_taxable', label: 'Taxable', align: 'right', format: money },
        { key: 'total_tax', label: 'Tax', align: 'right', format: money },
        { key: 'total_sales', label: 'Total', align: 'right', format: money },
      ]}
      totalsRow={(d) => {
        const r = d?.rows || [];
        return ['', 'TOTAL',
          num(r.reduce((s, x) => s + (x.bill_count || 0), 0)),
          money(r.reduce((s, x) => s + (x.total_discount || 0), 0)),
          money(r.reduce((s, x) => s + (x.total_taxable || 0), 0)),
          money(r.reduce((s, x) => s + (x.total_tax || 0), 0)),
          money(d?.total_sales || 0),
        ];
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// 3. Category wise
// ---------------------------------------------------------------------------
export function CategoryWiseReport() {
  return (
    <ReportShell
      title="Category wise Sales"
      url="/reports/sales/category-wise"
      responseKey="rows"
      csvName="category-wise-sales"
      columns={[
        { key: 'category_name', label: 'Category' },
        { key: 'bill_count', label: 'Bills', align: 'right', format: num },
        { key: 'qty_sold', label: 'Qty Sold', align: 'right', format: num },
        { key: 'taxable', label: 'Taxable', align: 'right', format: money },
        { key: 'tax', label: 'Tax', align: 'right', format: money },
        { key: 'total', label: 'Total', align: 'right', format: money },
      ]}
    />
  );
}

// ---------------------------------------------------------------------------
// 4. Brand wise
// ---------------------------------------------------------------------------
export function BrandWiseReport() {
  return (
    <ReportShell
      title="Brand wise Sales"
      url="/reports/sales/brand-wise"
      responseKey="rows"
      csvName="brand-wise-sales"
      columns={[
        { key: 'brand_name', label: 'Brand' },
        { key: 'bill_count', label: 'Bills', align: 'right', format: num },
        { key: 'qty_sold', label: 'Qty Sold', align: 'right', format: num },
        { key: 'taxable', label: 'Taxable', align: 'right', format: money },
        { key: 'tax', label: 'Tax', align: 'right', format: money },
        { key: 'total', label: 'Total', align: 'right', format: money },
      ]}
    />
  );
}

// ---------------------------------------------------------------------------
// 5. HSN wise
// ---------------------------------------------------------------------------
export function HsnWiseReport() {
  return (
    <ReportShell
      title="HSN wise Sales"
      url="/reports/sales/hsn-wise"
      responseKey="rows"
      csvName="hsn-wise-sales"
      columns={[
        { key: 'hsn_code', label: 'HSN' },
        { key: 'bill_count', label: 'Bills', align: 'right', format: num },
        { key: 'qty_sold', label: 'Qty', align: 'right', format: num },
        { key: 'taxable', label: 'Taxable', align: 'right', format: money },
        { key: 'cgst', label: 'CGST', align: 'right', format: money },
        { key: 'sgst', label: 'SGST', align: 'right', format: money },
        { key: 'igst', label: 'IGST', align: 'right', format: money },
        { key: 'total', label: 'Total', align: 'right', format: money },
      ]}
    />
  );
}

// ---------------------------------------------------------------------------
// 6. GST Tax
// ---------------------------------------------------------------------------
export function GstTaxReport() {
  return (
    <ReportShell
      title="GST Tax Report"
      url="/reports/sales/gst-tax"
      responseKey="rows"
      csvName="gst-tax-report"
      columns={[
        { key: 'tax_rate', label: 'Rate %', align: 'right', format: (v) => `${Number(v).toFixed(1)} %` },
        { key: 'qty_sold', label: 'Qty', align: 'right', format: num },
        { key: 'taxable', label: 'Taxable', align: 'right', format: money },
        { key: 'cgst', label: 'CGST', align: 'right', format: money },
        { key: 'sgst', label: 'SGST', align: 'right', format: money },
        { key: 'igst', label: 'IGST', align: 'right', format: money },
        { key: 'tax', label: 'Total Tax', align: 'right', format: money },
        { key: 'total', label: 'Total', align: 'right', format: money },
        { key: 'cash', label: 'Cash', align: 'right', format: money },
        { key: 'card', label: 'Card', align: 'right', format: money },
        { key: 'upi', label: 'UPI / Wallet', align: 'right', format: money },
      ]}
      totalsRow={(d) => {
        const t = d?.totals || {};
        return ['TOTAL', '', money(t.taxable), money(t.cgst), money(t.sgst), money(t.igst), money(t.tax), money(t.total), money(t.cash), money(t.card), money(t.upi)];
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// 7. Fast moving  &  8. Slow moving  &  9. Dead stock
// ---------------------------------------------------------------------------
export function FastMovingReport() {
  return (
    <ReportShell
      title="Fast Moving Products"
      url="/reports/sales/fast-moving"
      responseKey="rows"
      csvName="fast-moving"
      columns={[
        { key: 'sku', label: 'SKU' },
        { key: 'product_name', label: 'Product' },
        { key: 'qty_sold', label: 'Qty Sold', align: 'right', format: num },
        { key: 'total', label: 'Revenue', align: 'right', format: money },
      ]}
    />
  );
}

export function SlowMovingReport() {
  return (
    <ReportShell
      title="Slow Moving Products"
      url="/reports/sales/slow-moving"
      responseKey="rows"
      csvName="slow-moving"
      columns={[
        { key: 'sku', label: 'SKU' },
        { key: 'product_name', label: 'Product' },
        { key: 'qty_sold', label: 'Qty Sold', align: 'right', format: num },
        { key: 'total', label: 'Revenue', align: 'right', format: money },
      ]}
    />
  );
}

export function DeadStockReport() {
  return (
    <DeadStockInner />
  );
}

// Dead-stock has a unique window_days selector, so we specialise.
function DeadStockInner() {
  const [days, setDays] = useState(30);
  return (
    <ReportShell
      title={`Dead Stock — No Sale in last ${days} days`}
      url="/reports/sales/dead-stock"
      responseKey="rows"
      csvName={`dead-stock-${days}d`}
      showStoreFilter={true}
      buildParams={() => ({ window_days: days })}
      trigger={days}
      extra={
        <div>
          <label className="block text-xs text-gray-500 mb-1">Window</label>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} className="h-10 border border-gray-200 rounded px-3 text-sm bg-white" data-testid="dead-window-select">
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
        </div>
      }
      columns={[
        { key: 'sku', label: 'SKU' },
        { key: 'name', label: 'Product' },
        { key: 'barcode', label: 'Barcode' },
        { key: 'brand_name', label: 'Brand' },
        { key: 'category_name', label: 'Category' },
        { key: 'mrp', label: 'MRP', align: 'right', format: money },
      ]}
    />
  );
}

// Local helper so this file doesn't need an explicit useState import gating.
export { dateFmt };
